import React from 'react';
import { motion } from 'motion/react';
import { Image as ImageIcon, Send, Mic } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useTheme } from './ThemeProvider';
import { useUserData } from './UserDataContext';

interface PromptComposerProps {
  onClick?: () => void;
}

export function PromptComposer({ onClick }: PromptComposerProps) {
  const { theme } = useTheme();
  const { userProfile } = useUserData();

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      // The guide spotlights this card. Anchored on the card itself rather
      // than a wrapper div, so the cut-out matches what the reader sees:
      // a wrapper is a bare block box and does not share this element's
      // rounded corners or its mb-6, so the highlight sat slightly off.
      data-tour="guide-composer"
      className="p-5 rounded-[2rem] bg-[#f2deb3]/80 dark:bg-[#1a1a1a]/80 backdrop-blur-xl border border-black/10 dark:border-white/10 shadow-xl cursor-pointer mb-6"
    >
      <div className="flex items-start gap-4 mb-6">
        <Avatar className="w-11 h-11 border-2 border-[#F39C12] dark:border-[#4478ea] shrink-0 shadow-md">
          <AvatarImage src={userProfile?.portfolio?.[userProfile?.profilePictureIndex || 0]} />
          <AvatarFallback>{userProfile?.name?.[0] || 'U'}</AvatarFallback>
        </Avatar>
        <div className="pt-2">
          <span className="text-lg text-black/50 dark:text-white/40 font-bold leading-tight">
            Yo. What's on your mind...
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center border border-black/5 dark:border-white/10">
              <ImageIcon className="w-5 h-5 text-black/40 dark:text-white/40" />
            </div>
            <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center border border-black/5 dark:border-white/10">
              <Mic className="w-5 h-5 text-black/40 dark:text-white/40" />
            </div>
          </div>
          <span className="text-sm font-bold text-black/30 dark:text-white/20 ml-1">0 / 300</span>
        </div>

        <div 
          className="flex items-center justify-center gap-1.5 h-10 rounded-xl text-white font-black text-[10px] uppercase tracking-widest shadow-lg shrink-0"
          style={{ 
            background: theme === 'dark' 
              ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' 
              : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)',
            minWidth: '90px',
            paddingLeft: '20px',
            paddingRight: '20px'
          }}
        >
          <Send className="w-3 h-3 fill-current" />
          YAP
        </div>
      </div>
    </motion.div>
  );
}
